/**
 * AI-Enhanced Job Search Service
 * Integrates AI semantic analysis with existing Arbeidsplassen.no search
 * Maintains 100% backward compatibility with layered fallback strategy
 */

import { callDatabaseApi } from "./database-api-service";

export interface AIJobAnalysis {
  primaryJobCategories: string[];
  secondaryJobCategories: string[];
  searchTerms: string[];
  confidence: number;
  reasoning: string;
  personalityTraits: string[];
  skillPreferences: string[];
  workPreferences: string[];
  suggestedJobTypes: string[];
  additionalContext: {
    aiEnhanced: boolean;
    semanticMappingUsed: boolean;
    multiCategorySearch: boolean;
    personalityBasedMatching: boolean;
  };
}

export interface AIJobSearchResult {
  success: boolean;
  results: any[];
  metadata: {
    searchStrategy: 'ai-guided' | 'keyword-fallback' | 'basic-fallback';
    aiAnalysisUsed: boolean;
    aiAnalysis?: AIJobAnalysis;
    originalQuery: string;
    totalFound: number;
    queryTime: number;
  };
  formattedResponse: string;
}

/**
 * Call AI Parameter Interpreter for enhanced job semantic analysis
 */
async function getAIJobAnalysis(query: string): Promise<AIJobAnalysis | null> {
  console.log(`[AI-JOB-SEARCH] 🤖 Getting enhanced AI analysis for: "${query}"`);
  
  try {
    const supabaseUrl = 'https://zxkxyapayniimhpjetaa.supabase.co';
    const response = await fetch(`${supabaseUrl}/functions/v1/ai-parameter-interpreter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp4a3h5YXBheW5paW1ocGpldGFhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc5OTUzMjIsImV4cCI6MjA2MzU3MTMyMn0.VV7odKx4YQIM9EfzATDhVqb-RAofnjTp1tDyXP9-eLc'
      },
      body: JSON.stringify({
        query: query,
        context: 'norwegian_job_semantic_mapping'
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`[AI-JOB-SEARCH] ✅ Enhanced AI analysis successful with confidence: ${data.result?.confidence || 0}`);
      return data.result;
    } else {
      console.error(`[AI-JOB-SEARCH] ❌ AI analysis failed: ${response.status}`);
      return null;
    }
  } catch (error) {
    console.error(`[AI-JOB-SEARCH] ❌ AI analysis error:`, error);
    return null;
  }
}

/**
 * Convert AI job categories to intelligent search terms using comprehensive mapping
 */
function convertAIJobCategoriesToSearchTerms(aiAnalysis: AIJobAnalysis): string[] {
  const searchTerms = new Set<string>();
  
  // Add primary job categories (highest priority)
  aiAnalysis.primaryJobCategories.forEach(cat => {
    searchTerms.add(cat.toLowerCase());
  });
  
  // Add secondary job categories for broader coverage
  aiAnalysis.secondaryJobCategories.forEach(cat => {
    searchTerms.add(cat.toLowerCase());
  });
  
  // Add AI-identified search terms
  aiAnalysis.searchTerms.forEach(term => {
    searchTerms.add(term.toLowerCase());
  });
  
  // Add personality-based job terms if relevant
  if (aiAnalysis.personalityTraits.length > 0) {
    aiAnalysis.personalityTraits.forEach(trait => {
      if (trait.includes('kreativ') || trait.includes('kunstnerisk')) {
        searchTerms.add('design');
        searchTerms.add('marketing');
        searchTerms.add('kunst');
      }
      if (trait.includes('teknisk') || trait.includes('analytisk')) {
        searchTerms.add('it');
        searchTerms.add('utvikler');
        searchTerms.add('ingeniør');
      }
      if (trait.includes('sosial') || trait.includes('hjelpende')) {
        searchTerms.add('lærer');
        searchTerms.add('sykepleier');
        searchTerms.add('sosialarbeider');
      }
      if (trait.includes('leder') || trait.includes('organiserende')) {
        searchTerms.add('leder');
        searchTerms.add('prosjektleder');
        searchTerms.add('konsulent');
      }
    });
  }
  
  // Add skill-based terms
  if (aiAnalysis.skillPreferences.length > 0) {
    aiAnalysis.skillPreferences.forEach(skill => {
      searchTerms.add(skill.toLowerCase());
    });
  }
  
  return Array.from(searchTerms);
}

/**
 * Call existing Arbeidsplassen search via database API with preserved context
 */
async function searchArbeidsplassenViaAPI(originalQuery: string, aiEnhancedQuery?: string, maxResults: number = 15): Promise<any> {
  // TODO: This needs to be better. The edge function is capable of more complex queries.
  const queryToUse = aiEnhancedQuery || originalQuery;
  console.log(`[AI-JOB-SEARCH] 🔍 Calling database API for: "${queryToUse}"`);
  console.log(`[AI-JOB-SEARCH] 📋 Original context preserved: "${originalQuery}"`);
  
  try {
    const result = await callDatabaseApi({
      query: queryToUse,
      database: 'arbeidsplassen',
      maxResults: maxResults,
      // CRITICAL FIX: Send original query as context to preserve routing information
      parameters: {
        originalQuery: originalQuery,
        aiEnhanced: !!aiEnhancedQuery,
        preserveRouting: true
      }
    });
    
    console.log(`[AI-JOB-SEARCH] ✅ Database API call successful`);
    return result;
  } catch (error) {
    console.error(`[AI-JOB-SEARCH] ❌ Database API call failed:`, error);
    throw error;
  }
}

/**
 * AI-Enhanced Job Search with Intelligent Category Mapping
 */
export async function searchJobsWithAI(query: string, maxResults: number = 15): Promise<AIJobSearchResult> {
  const startTime = Date.now();
  console.log(`[AI-JOB-SEARCH] 🚀 Starting intelligent AI-enhanced job search for: "${query}"`);
  
  let aiAnalysis: AIJobAnalysis | null = null;
  let searchStrategy: 'ai-guided' | 'keyword-fallback' | 'basic-fallback' = 'keyword-fallback';
  
  // FASE 1: Try intelligent AI-guided search with enhanced analysis
  try {
    aiAnalysis = await getAIJobAnalysis(query);
    
    if (aiAnalysis && aiAnalysis.confidence > 0.6) {
      console.log(`[AI-JOB-SEARCH] 🎯 Enhanced AI analysis successful, trying intelligent search`);
      console.log(`[AI-JOB-SEARCH] 📊 AI insights:`, {
        primaryJobCategories: aiAnalysis.primaryJobCategories,
        personalityTraits: aiAnalysis.personalityTraits,
        skillPreferences: aiAnalysis.skillPreferences,
        workPreferences: aiAnalysis.workPreferences,
        confidence: aiAnalysis.confidence
      });
      
      // Convert AI categories to intelligent search terms

      // TODO: This smooshes a lot of stuff into a single simple query, and
      // we're also dropping a lot context here, including location. Yes, the
      // original query is attached, but it is OR'd with this query. 
      const aiSearchTerms = convertAIJobCategoriesToSearchTerms(aiAnalysis); 
      const aiQuery = aiSearchTerms.join(' ');
      
      console.log(`[AI-JOB-SEARCH] 🔍 Intelligent AI-guided query: "${aiQuery}"`);
      
      // CRITICAL FIX: Send both original query and AI-enhanced query
      const aiResult = await searchArbeidsplassenViaAPI(query, aiQuery, maxResults);

      console.log(`[AI-JOB-SEARCH] 📋 AI-guided search result:`, aiResult);
      
      if (aiResult.success && aiResult.result?.data?.jobs && aiResult.result.data.jobs.length > 0) {
        console.log(`[AI-JOB-SEARCH] ✅ Intelligent AI-guided search successful: ${aiResult.result.data.jobs.length} results`);
        
        return {
          success: true,
          results: aiResult.result.data.jobs,
          metadata: {
            searchStrategy: 'ai-guided',
            aiAnalysisUsed: true,
            aiAnalysis,
            originalQuery: query,
            totalFound: aiResult.result.data.jobs.length,
            queryTime: Date.now() - startTime
          },
          // formattedResponse: enhanceFormattedResponseWithAI(aiResult.formattedResponse, aiAnalysis)
          formattedResponse: aiResult.formattedResponse
        };
      }
    } else {
      console.log(`[AI-JOB-SEARCH] 🤔 AI analysis confidence too low (${aiAnalysis?.confidence || 0}), falling back`);
    }
  } catch (error) {
    console.error(`[AI-JOB-SEARCH] ❌ AI-guided search failed:`, error);
  }
  
  // FASE 2: Fallback to existing keyword search with preserved context
  console.log(`[AI-JOB-SEARCH] 🔄 Falling back to existing keyword search`);
  
  try {
    // CRITICAL FIX: Use original query for fallback to preserve context
    const keywordResult = await searchArbeidsplassenViaAPI(query, undefined, maxResults);
    
    if (keywordResult.success && keywordResult.result?.data?.jobs && keywordResult.result.data.jobs.length > 0) {
      console.log(`[AI-JOB-SEARCH] ✅ Keyword search successful: ${keywordResult.result.data.jobs.length} results`);
      
      return {
        success: true,
        results: keywordResult.result.data.jobs,
        metadata: {
          searchStrategy: 'keyword-fallback',
          aiAnalysisUsed: false,
          aiAnalysis: aiAnalysis || undefined,
          originalQuery: query,
          totalFound: keywordResult.result.data.jobs.length,
          queryTime: Date.now() - startTime
        },
        formattedResponse: keywordResult.formattedResponse
      };
    }
  } catch (error) {
    console.error(`[AI-JOB-SEARCH] ❌ Keyword search failed:`, error);
  }
  
  // FASE 3: Final fallback - broad job search with preserved context
  console.log(`[AI-JOB-SEARCH] 🔄 Final fallback to broad job search`);
  
  try {
    const broadQuery = 'jobb stilling'; // Very broad search
    // CRITICAL FIX: Still preserve original query context even in final fallback
    const broadResult = await searchArbeidsplassenViaAPI(query, broadQuery, maxResults);
    
    return {
      success: broadResult.success,
      results: broadResult.result?.data?.jobs || [],
      metadata: {
        searchStrategy: 'basic-fallback',
        aiAnalysisUsed: false,
        aiAnalysis: aiAnalysis || undefined,
        originalQuery: query,
        totalFound: broadResult.result?.data?.jobs?.length || 0,
        queryTime: Date.now() - startTime
      },
      formattedResponse: broadResult.formattedResponse || `## 💼 Jobbmuligheter

Jeg fant noen jobbmuligheter, men søket kunne forbedres. Prøv å være mer spesifikk om hva du ser etter.

**Søkte etter:** "${query}"

Besøk [Arbeidsplassen.no](https://arbeidsplassen.nav.no) for å utforske alle jobbmuligheter.`
    };
  } catch (error) {
    console.error(`[AI-JOB-SEARCH] ❌ All search strategies failed:`, error);
    
    return {
      success: false,
      results: [],
      metadata: {
        searchStrategy: 'basic-fallback',
        aiAnalysisUsed: false,
        originalQuery: query,
        totalFound: 0,
        queryTime: Date.now() - startTime
      },
      formattedResponse: `## ❌ Feil ved søk i Arbeidsplassen.no

Søket feilet. Prøv igjen eller gå til [Arbeidsplassen.no](https://arbeidsplassen.nav.no) direkte.

**Søkte etter:** "${query}"`
    };
  }
}

/**
 * Enhance formatted response with intelligent AI insights
 */
function enhanceFormattedResponseWithAI(originalResponse: string, aiAnalysis: AIJobAnalysis): string {
  const insights = [];
  
  if (aiAnalysis.personalityTraits.length > 0) {
    insights.push(`**🧠 Personlighetstrekk identifisert:** ${aiAnalysis.personalityTraits.join(', ')}`);
  }
  
  if (aiAnalysis.skillPreferences.length > 0) {
    insights.push(`**🛠️ Ferdighetspreferanser:** ${aiAnalysis.skillPreferences.join(', ')}`);
  }
  
  if (aiAnalysis.workPreferences.length > 0) {
    insights.push(`**💼 Arbeidsønsker:** ${aiAnalysis.workPreferences.join(', ')}`);
  }
  
  if (aiAnalysis.primaryJobCategories.length > 0) {
    insights.push(`**🎯 Prioriterte jobbkategorier:** ${aiAnalysis.primaryJobCategories.join(', ')}`);
  }
  
  const aiInsights = insights.length > 0 
    ? `\n\n### 🤖 Intelligent AI-analyse\n${insights.join('\n')}\n\n**Begrunnelse:** ${aiAnalysis.reasoning}`
    : '';
  
  return `${originalResponse}${aiInsights}\n\n---\n*🤖 Søk utført med intelligent AI-forbedret semantisk analyse (Confidence: ${Math.round(aiAnalysis.confidence * 100)}%)*`;
}
